import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../contexts/UserContext';
import { useModules } from '../contexts/ModuleContext';
import { Users, BookOpen, BarChart3, Settings, UserCheck, Award, Calendar, QrCode } from 'lucide-react';
import { graphqlService } from '../services/graphqlService';
import ModuleManagement from './ModuleManagement';
import TrainingScheduler from './TrainingScheduler';
import UserManagement from './UserManagement';
import TrainingAnalytics from './TrainingAnalytics';
import ModuleBarcodeManager from './ModuleBarcodeManager';
import BarcodeTestPage from './BarcodeTestPage';
import UsersListModal from './UsersListModal';
import SystemSettings from './SystemSettings';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { users, getUserStats } = useUsers();
  const { modules, getModuleStats } = useModules();
  const [currentView, setCurrentView] = useState('dashboard');
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    completedModules: 0,
    totalModules: 0,
    averageScore: 0
  });

  useEffect(() => {
    // Calculate dynamic statistics
    const loadStats = async () => {
      try {
        const [userStats, moduleStats] = await Promise.all([
          getUserStats(),
          getModuleStats()
        ]);
        
        // Get all progress records from database
        const allUsers = await graphqlService.getAllUsers();
        let completedModules = 0;
        let totalScore = 0;
        let scoreCount = 0;
        
        // Aggregate progress from all users
        for (const user of allUsers) {
          try {
            const progress = await graphqlService.getProgress(user.email);
            if (progress) {
              const progressValues = Object.values(progress);
              completedModules += progressValues.filter(p => p.completed && p.quizCompleted).length;
              
              const scores = progressValues
                .filter(p => p.quizScore !== undefined)
                .map(p => p.quizScore);
              
              if (scores.length > 0) {
                totalScore += scores.reduce((sum, score) => sum + score, 0);
                scoreCount += scores.length;
              }
            }
          } catch (error) {
            console.error(`Error loading progress for ${user.email}:`, error);
          }
        }
        
        const averageScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;
        
        setStats({
          totalUsers: userStats.totalUsers || users.length,
          completedModules,
          totalModules: moduleStats.totalModules || 0,
          averageScore
        });
      } catch (error) {
        console.error('Error loading admin stats:', error);
        // Fallback to basic stats
        setStats({
          totalUsers: users.length,
          completedModules: 0,
          totalModules: modules.length,
          averageScore: 0
        });
      }
    };
    
    loadStats();
  }, [users, modules, getUserStats, getModuleStats]);


  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleViewAllUsers = () => {
    setShowUsersModal(true);
  };

  if (currentView === 'module-management') {
    return <ModuleManagement onBack={handleBackToDashboard} />;
  }

  if (currentView === 'training-scheduler') {
    return <TrainingScheduler onBack={handleBackToDashboard} />;
  }

  if (currentView === 'user-management') {
    return <UserManagement onBack={handleBackToDashboard} />;
  }

  if (currentView === 'training-analytics') {
    return <TrainingAnalytics onBack={handleBackToDashboard} />;
  }

  if (currentView === 'module-barcodes') {
    return <ModuleBarcodeManager onBack={handleBackToDashboard} />;
  }

  if (currentView === 'barcode-test') {
    return <BarcodeTestPage onBack={handleBackToDashboard} />;
  }

  if (currentView === 'system-settings') {
    return <SystemSettings onBack={handleBackToDashboard} />;
  }

  return (
    <div className="container">
      <div className="admin-header">
        <div className="admin-welcome">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-lg text-gray-600">
            Welcome back, {user?.name} ({user?.role})
          </p>
        </div>
      </div>

      <div className="admin-stats">
        <div className="grid grid-4">
          <div className="card stat-card disabled">
            <div className="stat-icon">
              <Users size={32} color="#1e40af" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{stats.totalUsers}</h3>
              <p className="text-gray-600">Total Users</p>
            </div>
          </div>

          <div className="card stat-card">
            <div className="stat-icon">
              <BookOpen size={32} color="#059669" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{stats.completedModules}</h3>
              <p className="text-gray-600">Completed Modules</p>
            </div>
          </div>

          <div className="card stat-card">
            <div className="stat-icon">
              <BarChart3 size={32} color="#f59e0b" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{stats.averageScore}%</h3>
              <p className="text-gray-600">Average Score</p>
            </div>
          </div>

          <div className="card stat-card">
            <div className="stat-icon">
              <Award size={32} color="#7c3aed" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{stats.totalModules}</h3>
              <p className="text-gray-600">Total Modules</p>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-sections">
        <div className="grid grid-3">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">User Management</h2>
            <p className="text-gray-600 mb-4">Manage staff and their training progress.</p>
            <button 
              onClick={() => handleViewChange('user-management')}
              className="btn btn-primary"
            >
              <Users size={16} style={{ marginRight: '8px' }} />
              Manage Users
            </button>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">Training Analytics</h2>
            <p className="text-gray-600 mb-4">View detailed reports and analytics on training progress.</p>
            <button 
              onClick={() => handleViewChange('training-analytics')}
              className="btn btn-primary"
            >
              <BarChart3 size={16} style={{ marginRight: '8px' }} />
              View Analytics
            </button>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">Module Management</h2>
            <p className="text-gray-600 mb-4">Create, edit, and manage training modules.</p>
            <button 
              onClick={() => handleViewChange('module-management')}
              className="btn btn-primary"
            >
              <BookOpen size={16} style={{ marginRight: '8px' }} />
              Manage Modules
            </button>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">Training Scheduler</h2>
            <p className="text-gray-600 mb-4">Schedule training sessions and assign them to employees.</p>
            <button 
              onClick={() => handleViewChange('training-scheduler')}
              className="btn btn-primary"
            >
              <Calendar size={16} style={{ marginRight: '8px' }} />
              Schedule Training
            </button>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">Module Barcodes</h2>
            <p className="text-gray-600 mb-4">Generate QR codes for each training module for easy access.</p>
            <div className="card-actions">
              <button 
                onClick={() => handleViewChange('module-barcodes')}
                className="btn btn-primary"
              >
                <QrCode size={16} style={{ marginRight: '8px' }} />
                Manage Barcodes
              </button>
              <button 
                onClick={() => handleViewChange('barcode-test')}
                className="btn btn-secondary test-system"
              >
                Test System
              </button>
            </div>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">System Settings</h2>
            <p className="text-gray-600 mb-4">Configure system settings and preferences.</p>
            <button 
              onClick={() => handleViewChange('system-settings')}
              className="btn btn-primary"
            >
              <Settings size={16} style={{ marginRight: '8px' }} />
              Settings
            </button>
          </div>
        </div>
      </div>

      <UsersListModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
      />
    </div>
  );
};

export default AdminDashboard;
